import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAlert } from '../components/GlobalAlert';
import authApi from '../api/auth';

function HelpPage() {
  const { t } = useLanguage();
  const { showAlert } = useAlert();
  const [message, setMessage] = useState('');
  const [subject, setSubject] = useState('');
  const [loading, setLoading] = useState(false);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 0,
    totalMessages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });

  useEffect(() => {
    fetchHelpMessages();
  }, []);

  const fetchHelpMessages = async (page = 1) => {
    try {
      setMessagesLoading(true);
      const response = await authApi.getHelpMessages(page, 10);
      setMessages(response.messages);
      setPagination(response.pagination);
    } catch (err) {
      console.error('Failed to fetch help messages:', err);
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!message.trim()) {
      showAlert(t('messageRequired'), 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await authApi.submitHelpMessage(message.trim(), subject.trim() || 'Help Request');
      
      if (response.success) {
        showAlert('✅ ' + t('messageSentSuccess'), 'success');
        setMessage('');
        setSubject('');
        // Refresh messages list
        fetchHelpMessages();
      }
    } catch (err) {
      showAlert('❌ ' + (err.message || t('messageSentError')), 'error');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('th-TH', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return t('pending');
      case 'in-progress': return t('inProgress');
      case 'resolved': return t('resolved');
      default: return status;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#ffc107';
      case 'in-progress': return '#17a2b8';
      case 'resolved': return '#28a745';
      default: return '#6c757d';
    }
  };

  const handlePageChange = (newPage) => {
    fetchHelpMessages(newPage);
  };

  const styles = {
    card: {
      backgroundColor: 'white',
      borderRadius: '10px',
      padding: '30px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      maxWidth: '1000px',
      margin: '0 auto',
    },
    title: {
      textAlign: 'center',
      marginBottom: '30px',
      color: '#333',
      fontSize: '28px',
      fontWeight: 'bold',
    },
    subtitle: {
      textAlign: 'center',
      fontSize: '16px',
      color: '#666',
      marginBottom: '30px',
    },
    form: {
      marginBottom: '40px',
    },
    formGroup: {
      marginBottom: '20px',
    },
    label: {
      display: 'block',
      marginBottom: '8px',
      fontWeight: '500',
      color: '#374151',
      fontSize: '14px',
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      transition: 'border-color 0.2s ease',
      outline: 'none',
      boxSizing: 'border-box',
    },
    textarea: {
      width: '100%',
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: '8px',
      fontSize: '16px',
      transition: 'border-color 0.2s ease',
      outline: 'none',
      boxSizing: 'border-box',
      minHeight: '120px',
      resize: 'vertical',
    },
    submitButton: {
      backgroundColor: '#3b82f6',
      color: 'white',
      border: 'none',
      padding: '12px 24px',
      borderRadius: '8px',
      fontSize: '16px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'background-color 0.2s ease',
    },
    submitButtonDisabled: {
      backgroundColor: '#9ca3af',
      cursor: 'not-allowed',
    },
    messagesSection: {
      marginTop: '40px',
    },
    messagesTitle: {
      fontSize: '20px',
      fontWeight: '600',
      color: '#333',
      marginBottom: '20px',
      borderBottom: '2px solid #e5e7eb',
      paddingBottom: '10px',
    },
    messageCard: {
      backgroundColor: '#f8f9fa',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      padding: '20px',
      marginBottom: '15px',
    },
    messageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '10px',
    },
    messageSubject: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#333',
    },
    messageDate: {
      fontSize: '14px',
      color: '#666',
    },
    messageContent: {
      fontSize: '14px',
      color: '#374151',
      lineHeight: '1.5',
      marginBottom: '10px',
    },
    messageStatus: {
      display: 'inline-block',
      padding: '4px 8px',
      borderRadius: '4px',
      fontSize: '12px',
      fontWeight: '500',
      color: 'white',
    },
    adminResponse: {
      backgroundColor: '#e3f2fd',
      border: '1px solid #bbdefb',
      borderRadius: '6px',
      padding: '15px',
      marginTop: '10px',
    },
    adminResponseTitle: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#1976d2',
      marginBottom: '8px',
    },
    adminResponseContent: {
      fontSize: '14px',
      color: '#374151',
      lineHeight: '1.5',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#666',
      fontSize: '16px',
    },
    emptyState: {
      textAlign: 'center',
      padding: '40px',
      color: '#666',
      fontSize: '16px',
    },
    pagination: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
      marginTop: '30px',
    },
    paginationButton: {
      padding: '8px 16px',
      border: '1px solid #dee2e6',
      backgroundColor: 'white',
      color: '#495057',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      transition: 'all 0.2s ease',
    },
    paginationButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
    paginationInfo: {
      fontSize: '14px',
      color: '#666',
      margin: '0 20px',
    },
  };

  return (
    <div style={styles.card}>
      <h1 style={styles.title}>{t('help')}</h1>
      <p style={styles.subtitle}>
        {t('helpDescription')}
      </p>

      {/* Submit Form */}
      <form onSubmit={handleSubmit} style={styles.form}>
        <div style={styles.formGroup}>
          <label htmlFor="subject" style={styles.label}>{t('subject')}</label>
          <input
            type="text"
            id="subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('enterSubject')}
            style={styles.input}
          />
        </div>

        <div style={styles.formGroup}>
          <label htmlFor="message" style={styles.label}>{t('message')}</label>
          <textarea
            id="message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder={t('enterMessage')}
            style={styles.textarea}
            required
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          style={loading ? { ...styles.submitButton, ...styles.submitButtonDisabled } : styles.submitButton}
        >
          {loading ? t('sending') : t('submit')}
        </button>
      </form>

      {/* Messages History */}
      <div style={styles.messagesSection}>
        <h2 style={styles.messagesTitle}>{t('messageHistory')}</h2>
        
        {messagesLoading ? (
          <div style={styles.loading}>
            📝 {t('loading')}...
          </div>
        ) : messages.length === 0 ? (
          <div style={styles.emptyState}>
            📝 {t('noMessages')}
          </div>
        ) : (
          <>
            {messages.map((msg) => (
              <div key={msg._id} style={styles.messageCard}>
                <div style={styles.messageHeader}>
                  <span style={styles.messageSubject}>{msg.subject}</span>
                  <span style={styles.messageDate}>{formatDate(msg.createdAt)}</span>
                </div>
                <div style={styles.messageContent}>{msg.message}</div>
                <span 
                  style={{
                    ...styles.messageStatus,
                    backgroundColor: getStatusColor(msg.status)
                  }}
                >
                  {getStatusText(msg.status)}
                </span>
                
                {msg.adminResponse && (
                  <div style={styles.adminResponse}>
                    <div style={styles.adminResponseTitle}>{t('adminResponse')}</div>
                    <div style={styles.adminResponseContent}>{msg.adminResponse}</div>
                    {msg.respondedAt && (
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '8px' }}>
                        {formatDate(msg.respondedAt)}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            {pagination.totalPages > 1 && (
              <div style={styles.pagination}>
                <button
                  style={{
                    ...styles.paginationButton,
                    ...(pagination.currentPage === 1 && styles.paginationButtonDisabled)
                  }}
                  onClick={() => handlePageChange(pagination.currentPage - 1)}
                  disabled={pagination.currentPage === 1}
                >
                  {t('previous')}
                </button>
                
                <span style={styles.paginationInfo}>
                  {t('page')} {pagination.currentPage} {t('of')} {pagination.totalPages}
                </span>
                
                <button
                  style={{
                    ...styles.paginationButton,
                    ...(pagination.currentPage === pagination.totalPages && styles.paginationButtonDisabled)
                  }}
                  onClick={() => handlePageChange(pagination.currentPage + 1)}
                  disabled={pagination.currentPage === pagination.totalPages}
                >
                  {t('next')}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default HelpPage; 